/**
 * ProblemSection — section 5 of the homepage (Konzept § 5 Z. 130).
 *
 * Job: dock the cold, problem-driven visitor, in their own language.
 *
 * Deliberately placed *after* the cornerstones: the dominant visitor is the warm
 * contact looking for confirmation, so the problem block does not lead the page
 * (a considered departure from journai's problem-first structure, Konzept Z. 130).
 *
 * Trug lange gar keinen Ausgang — die Eckpfeiler-Karten darueber waren gemeint.
 *
 * Revidiert (Jan, 3.8.2026): ein **Textlink** auf `/angebote`, bewusst kein
 * Button. Der urspruengliche Grund gilt weiter — der primaere CTA soll hier
 * nicht konkurrieren, er gehoert Header und Abschluss-Block (Konzept Z. 135).
 * Wer den Abschnitt aber zu Ende liest, hat gerade sein Problem wiedererkannt
 * und braucht den Weg zum Angebot, ohne zu den Karten zurueckzuscrollen.
 *
 * Der Link ist optional: ohne `linkHref` rendert die Sektion wie zuvor.
 */

import React from "react";
import { Link } from "react-router-dom";
import { type SectionProblemProps } from "../../content/section-problem";
import { useLocalizedUrl } from "../../hooks";
import { SectionContainer } from "../ui";

export type ProblemSectionProps = SectionProblemProps & {
    /**
     * Teil eines Kapitels statt eigenstaendige Sektion? (12.8.2026)
     *
     * Im Kapitel «Unser Ansatz» der Startseite ist DIESE Ueberschrift die
     * Kapitelueberschrift — sie steht eine Ebene hoeher in `ChapterSection`,
     * und «So arbeiten wir» folgt darunter als `h3`. Der Absatz hier sagt, WAS
     * wir bauen, der darunter WIE; zwei Ausgaenge bleiben, einer je Teil.
     *
     * `nested` laesst deshalb die eigene Ueberschrift weg und legt Flaeche und
     * Aussenabstand ab; beides setzt das Kapitel.
     */
    nested?: boolean;
};

export const ProblemSection: React.FC<ProblemSectionProps> = ({
    title,
    description,
    linkText,
    linkHref,
    nested = false,
}) => {
    // Vor jedem bedingten Ausstieg — sonst laufen bei verlinkter und
    // unverlinkter Sektion verschiedene Hooks (Rules of Hooks).
    const localizedHref = useLocalizedUrl(linkHref ?? "/angebote");

    return (
        <section data-testid="problem-section" className={nested ? "" : "bg-gray-50 py-24"}>
            {/* Zentriert wie die uebrigen reinen Textsektionen (Jan, 5.8.2026).
                Vorher `max-w-3xl` OHNE `mx-auto`: der Block klebte links, waehrend
                Nachbarsektionen ohne Karten oder Liste mittig standen. */}
            <SectionContainer width="text" className="text-center">
                <div>
                    {!nested && <h2 className="mb-6 text-3xl font-black text-gray-900 md:text-4xl">{title}</h2>}
                    <p className="text-lg leading-relaxed text-gray-600">{description}</p>
                    {linkHref && linkText && (
                        <Link
                            to={localizedHref}
                            className="mt-6 inline-flex items-center gap-1 text-lg font-semibold text-primary
                                underline-offset-4 hover:underline focus:outline-none focus-visible:ring-2
                                focus-visible:ring-primary"
                        >
                            {linkText}
                            <span aria-hidden="true">→</span>
                        </Link>
                    )}
                </div>
            </SectionContainer>
        </section>
    );
};

export default ProblemSection;
